// src/sections/FlowsSection.jsx
import React, { useEffect, useRef, useState } from "react";
import MetricCard from "../components/MetricCard";
import ChartModal from "../components/ChartModal";
import "../components/Overview.css";
import { apiUrl } from "../apiBase";

const FLOWS_METRICS = {
  // 1) System Liquidity
  walcl: {
    id: "walcl",
    title: "Fed Balance Sheet",
    subtitle: "Total assets (approx, $T)",
    icon: "🏦",
    unit: "trillions",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1QTa2",
    description:
      "Fed total assets (WALCL) is a clean proxy for QE/QT. Rising assets usually add liquidity; falling assets usually drain liquidity over time.",
  },
  reserves: {
    id: "reserves",
    title: "Federal Bank Reserves",
    subtitle: "Reserve balances (approx, $B)",
    icon: "💧",
    unit: "index",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1QZ4w",
    description:
      "Reserve balances reflect system liquidity inside the banking system. Falling reserves can signal tighter conditions, especially if paired with shrinking Fed assets.",
  },
  tga: {
    id: "tga",
    title: "Treasury General Account",
    subtitle: "Gov cash balance (approx, $B)",
    icon: "🏛️",
    unit: "index",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1QZ4E",
    description:
      "TGA is the Treasury’s cash account. When TGA rises quickly, it can drain liquidity; when it falls, it can add liquidity back into the system.",
  },
  rrp: {
    id: "rrp",
    title: "Reverse Repo (ON RRP)",
    subtitle: "Money parked at the Fed",
    icon: "🧲",
    unit: "index",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1QZ51",
    description:
      "Overnight RRP is a liquidity sink. High usage can mean excess cash is being absorbed by the Fed; falling usage can mean liquidity is flowing back out.",
  },

  // 2) Financial Conditions / Funding
  sofr: {
    id: "sofr",
    title: "SOFR",
    subtitle: "Overnight funding rate",
    icon: "💵",
    unit: "percent",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1QZ5S",
    description:
      "SOFR is the key overnight funding rate proxy. Spikes or persistent elevation can signal tighter funding conditions.",
  },
  nfci: {
    id: "nfci",
    title: "NFCI",
    subtitle: "Financial conditions index",
    icon: "🎛️",
    unit: "index",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1QZ61",
    description:
      "NFCI summarizes a wide set of credit and funding indicators. Higher values generally mean tighter conditions; lower values mean easier conditions.",
  },
  anfci: {
    id: "anfci",
    title: "Adj. NFCI",
    subtitle: "Adjusted financial conditions",
    icon: "🧭",
    unit: "index",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1QZ6a",
    description:
      "Adjusted NFCI strips out cyclical influences. It’s a cleaner view of whether financial conditions are structurally tight or easy.",
  },

  // 3) Capital / Cash proxies
  mmmf: {
    id: "mmmf",
    title: "Money Market Fund Assets",
    subtitle: "Cash on the sidelines",
    icon: "💰",
    unit: "index",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1QVnN",
    description:
      "Money market fund assets reflect how much cash is sitting in short-term instruments. Rapid growth can mean risk-off positioning or attractive cash yields.",
  },
  bankCredit: {
    id: "bankCredit",
    title: "Commercial Bank Credit",
    subtitle: "Credit creation proxy",
    icon: "📈",
    unit: "index",
    embedUrl: "https://fred.stlouisfed.org/graph/?g=1QZ6s",
    description:
      "Bank credit growth is a broad proxy for lending and credit creation. Slowing growth can mean tighter credit availability and weaker liquidity impulse.",
  },
};

const SYSTEM_LIQUIDITY_IDS = ["walcl", "reserves", "tga", "rrp"];
const CONDITIONS_IDS = ["sofr", "nfci", "anfci"];
const CAPITAL_IDS = ["mmmf", "bankCredit"];

const REGIME_INFO = {
  title: "Liquidity Regime (Color Guide)",
  description: (
    <div className="modal-doc">
      <p>
        This is a simple composite label to summarize liquidity direction. It’s a quick
        “tailwind vs headwind” check — <b>not a trading signal</b>.
      </p>

      <hr />

      <h3>Inputs</h3>
      <ul>
        <li><b>Fed assets / reserves</b> (liquidity supply)</li>
        <li><b>TGA + RRP</b> (liquidity drains)</li>
        <li><b>Financial conditions (ANFCI)</b> (tightening vs easing)</li>
      </ul>

      <hr />

      <h3>Color meanings</h3>
      <ul>
        <li>
          <b>Green = Liquidity Expanding (tailwind)</b>
          <ul>
            <li>Fed assets/reserves rising and/or drains easing</li>
            <li>Conditions (ANFCI) not tightening much</li>
          </ul>
        </li>

        <li>
          <b>Yellow = Neutral / Mixed</b>
          <ul>
            <li>Signals conflict (some improving, some draining)</li>
            <li>Good for “wait for confirmation” mindset</li>
          </ul>
        </li>

        <li>
          <b>Red = Liquidity Tightening (headwind)</b>
          <ul>
            <li>Fed assets/reserves falling and/or drains rising</li>
            <li>Conditions (ANFCI) tightening</li>
          </ul>
        </li>
      </ul>

      <hr />

      <h3>Quick checklist</h3>
      <ul>
        <li><b>Assets/Reserves:</b> rising or falling?</li>
        <li><b>TGA/RRP:</b> draining less or draining more?</li>
        <li><b>ANFCI:</b> easing or tightening?</li>
      </ul>

      <p style={{ marginTop: 12 }}>
        <i>Note: This is a composite label for context, not a trading signal.</i>
      </p>
    </div>
  ),
};

const clamp = (n, a, b) => Math.min(Math.max(n, a), b);

// ✅ Traffic light capsule (like your 2nd screenshot)
function TrafficLight({ score }) {
  const s =
    score == null || Number.isNaN(Number(score)) ? 55 : clamp(Number(score), 0, 100);

  const isGreen = s >= 65;
  const isRed = s <= 45;
  const isYellow = !isGreen && !isRed;

  const DOT_SIZE = 34;
  const on = 1;
  const off = 0.35;

  const dot = (active, color) => ({
    width: DOT_SIZE,
    height: DOT_SIZE,
    borderRadius: 999,
    background: color,
    opacity: active ? on : off,
    boxShadow: active
      ? "inset 0 1px 0 rgba(255,255,255,0.18), 0 6px 14px rgba(0,0,0,0.35)"
      : "inset 0 1px 0 rgba(255,255,255,0.10)",
  });

  const capsuleStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 18,
    padding: "14px 18px",
    borderRadius: 999,
    background: "rgba(17, 24, 39, 0.95)",
    border: "6px solid rgba(148, 163, 184, 0.55)",
    boxShadow: "0 10px 24px rgba(0,0,0,0.25)",
  };

  const statusText = isGreen
    ? "Liquidity tailwind"
    : isRed
    ? "Liquidity headwind"
    : "Mixed / wait for confirmation";

  return (
    <div style={{ marginTop: 12 }}>
      <div style={capsuleStyle}>
        <div style={dot(isRed, "#ef4444")} />
        <div style={dot(isYellow, "#facc15")} />
        <div style={dot(isGreen, "#22c55e")} />
      </div>

      <div style={{ marginTop: 10, color: "var(--text-secondary)", fontSize: "0.85rem" }}>
        {statusText}
      </div>
    </div>
  );
}

// Info modal
const InfoModal = ({ isOpen, metric, onClose }) => {
  if (!metric) return null;
  return (
    <div
      className={`modal-backdrop ${isOpen ? "modal-open" : "modal-closed"}`}
      onClick={onClose}
    >
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>How to read: {metric.title}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close info">
            ×
          </button>
        </div>
        <div className="modal-description">
          {metric.description
            ? typeof metric.description === "string"
              ? <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{metric.description}</p>
              : metric.description
            : "No explanation configured yet."}
        </div>
      </div>
    </div>
  );
};

const FlowsSection = ({ onConnectionStatus }) => {
  const [map, setMap] = useState(null);
  const [asOf, setAsOf] = useState(null);
  const [regime, setRegime] = useState(null);
  const [loading, setLoading] = useState(true);

  const [activeMetric, setActiveMetric] = useState(null);
  const [isChartOpen, setIsChartOpen] = useState(false);

  const [activeInfoMetric, setActiveInfoMetric] = useState(null);
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  const onConnectionStatusRef = useRef(onConnectionStatus);
  useEffect(() => {
    onConnectionStatusRef.current = onConnectionStatus;
  }, [onConnectionStatus]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetch(apiUrl("/api/flows/overview"));
        if (!res.ok) throw new Error("Failed to fetch flows data");
        const json = await res.json();
        if (cancelled) return;

        const nextMap = {};
        (json.metrics || []).forEach((m) => {
          nextMap[m.id] = {
            value: m.latestValue ?? null,
            prevValue: m.prevValue ?? null,
            status: m.error ? "error" : "ok",
            timestamp: m.latestDate || null,
          };
        });

        setMap(nextMap);
        setAsOf(json.asOf || null);
        setRegime(json.regime || null);

        if (onConnectionStatusRef.current) onConnectionStatusRef.current(true);
      } catch (e) {
        console.error("Flows fetch error:", e);
        if (!cancelled) {
          setMap(null);
          setAsOf(null);
          setRegime(null);
        }
        if (onConnectionStatusRef.current) onConnectionStatusRef.current(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    const interval = setInterval(load, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const openChart = (cfg) => {
    setActiveMetric(cfg);
    setIsChartOpen(true);
  };
  const closeChart = () => {
    setIsChartOpen(false);
    setActiveMetric(null);
  };

  const openInfo = (cfg) => {
    setActiveInfoMetric(cfg);
    setIsInfoOpen(true);
  };
  const closeInfo = () => {
    setIsInfoOpen(false);
    setActiveInfoMetric(null);
  };

  const renderGrid = (ids) => (
    <div className="metrics-grid">
      {ids.map((id) => {
        const cfg = FLOWS_METRICS[id];
        if (!cfg) return null;
        return (
          <MetricCard
            key={id}
            metric={cfg}
            data={map?.[id] || null}
            onClick={() => openChart(cfg)}
            onInfoClick={() => openInfo(cfg)}
          />
        );
      })}
    </div>
  );

  if (loading && !map) {
    return (
      <div className="overview-loading">
        <div className="loading-spinner" />
        <p>Loading flows & liquidity...</p>
      </div>
    );
  }

  // Fallback so we never show "—"
  const safeRegime =
    regime && typeof regime === "object"
      ? regime
      : {
          label: "🟡 Neutral / Mixed",
          score: 55,
          description:
            "Mixed signals. Watch whether reserves and Fed assets trend together, and whether conditions drift tighter or easier.",
        };

  const bannerTitle = safeRegime.label || "🟡 Neutral / Mixed";
  const bannerText =
    safeRegime.description ||
    "This section tracks system liquidity and financial conditions. Focus on direction and trends rather than single prints.";

  const scoreNum =
    safeRegime.score == null || Number.isNaN(Number(safeRegime.score))
      ? 55
      : clamp(Number(safeRegime.score), 0, 100);

  return (
    <div className="overview">
      {/* Banner */}
      <div className="summary-card market-tone">
        <h3>{bannerTitle}</h3>
        <p>
          {bannerText} {asOf ? ` (As of ${asOf})` : ""}
        </p>
      </div>

      {/* Block 1 */}
      <section className="overview-block">
        <div className="block-header">
          <h2>System Liquidity</h2>
          <p>Fed assets, reserves, TGA, and RRP — the plumbing that often drives “liquidity impulse.”</p>
        </div>
        {renderGrid(SYSTEM_LIQUIDITY_IDS)}

        <div className="fun-fact">
          <div className="fun-fact-inner">
            <h4>How to read this</h4>
            <p>
              If Fed assets and reserves trend up, liquidity is usually improving. Rising TGA or high RRP can act like drains.
              Watch the direction over weeks, not one data point.
            </p>
          </div>
        </div>
      </section>

      {/* Block 2 */}
      <section className="overview-block">
        <div className="block-header">
          <h2>Financial Conditions</h2>
          <p>Funding and broad conditions: SOFR + (Adj.) NFCI.</p>
        </div>
        {renderGrid(CONDITIONS_IDS)}

        <div className="fun-fact">
          <div className="fun-fact-inner">
            <h4>How to read this</h4>
            <p>
              NFCI / ANFCI rising usually means tighter conditions. If conditions tighten while liquidity drains, risk assets often struggle.
              If conditions ease while liquidity improves, markets tend to act more “risk-on.”
            </p>
          </div>
        </div>
      </section>

      {/* Block 3 */}
      <section className="overview-block">
        <div className="block-header">
          <h2>Capital & Cash Proxies</h2>
          <p>Where the cash is sitting and whether credit creation is accelerating or slowing.</p>
        </div>
        {renderGrid(CAPITAL_IDS)}

        <div className="yield-curve-section" style={{ marginTop: 24 }}>
          <div className="chart-card">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div className="chart-title">Liquidity Regime</div>

              <button
                onClick={() => openInfo(REGIME_INFO)}
                title="What do the colors mean?"
                aria-label="Liquidity regime color guide"
                style={{
                  border: "1px solid var(--border-light)",
                  background: "var(--bg-card)",
                  color: "var(--text-secondary)",
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  cursor: "pointer",
                  fontWeight: 900,
                  lineHeight: "28px",
                }}
              >
                i
              </button>
            </div>

            <div style={{ color: "var(--text-secondary)", fontSize: "0.85rem", marginBottom: 10 }}>
              Simple composite label to summarize direction (not a trading signal).
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <div style={{ fontWeight: 800, color: "var(--text-primary)" }}>{safeRegime.label}</div>
              <div style={{ fontWeight: 800, color: "var(--text-primary)" }}>
                {Math.round(scoreNum)}/100
              </div>
            </div>

            <TrafficLight score={scoreNum} />

            <div style={{ marginTop: 10, color: "var(--text-secondary)", fontSize: "0.85rem" }}>
              {safeRegime.description}
            </div>
          </div>

          <div className="chart-card">
            <div className="chart-title">What to watch</div>
            <div style={{ color: "var(--text-secondary)", lineHeight: 1.6 }}>
              A quick checklist:
              <span style={{ display: "block", marginTop: 8 }}>
                • Fed assets + reserves (direction)
              </span>
              <span style={{ display: "block" }}>
                • TGA/RRP (liquidity drains vs releases)
              </span>
              <span style={{ display: "block" }}>
                • ANFCI (tightening vs easing)
              </span>
            </div>
          </div>
        </div>

        <div className="fun-fact">
          <div className="fun-fact-inner">
            <h4>How to read this</h4>
            <p>
              Money market assets rising can mean “cash on the sidelines.” Bank credit growth slowing can mean tighter lending.
              Combine these with System Liquidity + Conditions for the full picture.
            </p>
          </div>
        </div>
      </section>

      {/* Modals */}
      <ChartModal isOpen={isChartOpen} metric={activeMetric} onClose={closeChart} />
      <InfoModal isOpen={isInfoOpen} metric={activeInfoMetric} onClose={closeInfo} />
    </div>
  );
};

export default FlowsSection;